/**
 * Builds a URL with query parameters
 *
 * @param baseUrl The base URL without query parameters
 * @param params An object containing the query parameters
 * @returns A URL with properly formatted query parameters
 *
 * @example
 * // Returns "https://api.example.com/endpoint?param1=value1&param2=value2"
 * buildUrlWithParams("https://api.example.com/endpoint", { param1: "value1", param2: "value2" });
 *
 * // Handles undefined, null, and string 'null' values by skipping them
 */
export const buildUrlWithParams = (baseUrl: string, params: Record<string, unknown>): string => {
  // Filter out undefined, null, empty strings, and the literal string 'null'.
  // Preserve 0 and other falsy-but-valid values (e.g., limit=0 is a valid
  // API parameter that was previously filtered out because 0 is falsy).
  const validParams = Object.entries(params).filter(
    ([_, value]) => value !== null && value !== undefined && value !== '' && value !== 'null',
  );

  if (validParams.length === 0) return baseUrl;

  // Build query string with proper separator
  const queryString = validParams
    .map(([key, value], index) => {
      const prefix = index === 0 ? '?' : '&';
      return `${prefix}${key}=${encodeURIComponent(String(value))}`;
    })
    .join('');

  return `${baseUrl}${queryString}`;
};

export default buildUrlWithParams;
